import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Play, ArrowRight, Heart, Shield, DollarSign, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import EnrollmentAIChat from '@/components/ai/EnrollmentAIChat';
import { toast } from 'sonner';

export default function EnrollmentIntro() {
  const [employee, setEmployee] = useState(null);
  const [employer, setEmployer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = await api.auth.me();
      const employees = await api.entities.Employee.filter({ user_email: user.email });
      
      if (employees.length > 0) {
        setEmployee(employees[0]);
        // Mark enrollment step for resume
        await api.entities.Employee.update(employees[0].id, {
          last_enrollment_step: 'intro'
        });
        const employers = await api.entities.Employer.filter({ id: employees[0].employer_id });
        if (employers.length > 0) setEmployer(employers[0]);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to load enrollment intro');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4"
        >
          <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
            Welcome to Your Benefits Enrollment
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Hi {employee?.first_name}! Let's find the perfect benefits package for you and your family.
          </p>
        </motion.div>

        {/* Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="overflow-hidden border-2 border-emerald-200">
            <CardContent className="p-0">
              {showVideo && employer?.welcome_video_url ? (
                <div className="aspect-video">
                  <iframe
                    src={employer.welcome_video_url}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              ) : (
                <div className="relative aspect-video bg-gradient-to-r from-emerald-500 to-teal-500">
                  <div className="absolute inset-0 flex items-center justify-center">
                    {employer?.welcome_video_url ? (
                      <button
                        onClick={() => setShowVideo(true)}
                        className="w-24 h-24 rounded-full bg-white/90 flex items-center justify-center hover:scale-110 transition-transform shadow-2xl"
                      >
                        <Play className="w-10 h-10 text-emerald-600 ml-2" />
                      </button>
                    ) : (
                      <div className="text-center text-white p-8">
                        <Shield className="w-20 h-20 mx-auto mb-4 opacity-90" />
                        <h2 className="text-3xl font-bold mb-2">Your Benefits Await</h2>
                        <p className="text-lg opacity-90">Choose the coverage that's right for you</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* What to Expect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid md:grid-cols-3 gap-6"
        >
          <Card className="bg-white border-2 border-slate-200 hover:border-emerald-300 transition-all">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mx-auto">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg">Browse Benefits</h3>
              <p className="text-sm text-slate-600">
                Explore health, dental, life insurance, and more - all personalized for you
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-slate-200 hover:border-emerald-300 transition-all">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mx-auto">
                <DollarSign className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg">See Tax Savings</h3>
              <p className="text-sm text-slate-600">
                Section 125 benefits reduce your taxes - see exactly how much you'll save
              </p>
            </CardContent>
          </Card>

          <Card className="bg-white border-2 border-slate-200 hover:border-emerald-300 transition-all">
            <CardContent className="p-6 text-center space-y-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg">Simple Enrollment</h3>
              <p className="text-sm text-slate-600">
                Select benefits, review your choices, sign electronically - done in minutes
              </p>
            </CardContent>
          </Card>
        </motion.div>

        {/* What's Included */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <Card className="bg-gradient-to-br from-white to-emerald-50 border-2 border-emerald-200">
            <CardContent className="p-8 space-y-6">
              <h2 className="text-2xl font-bold text-slate-900 text-center">What You'll Experience</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">Personalized Pricing</p>
                    <p className="text-sm text-slate-600">Rates calculated specifically for your age, location, and family</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">AI Assistance</p>
                    <p className="text-sm text-slate-600">Victoria is here 24/7 to answer questions and guide you</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">Tax Savings Calculator</p>
                    <p className="text-sm text-slate-600">See your annual tax savings with pre-tax Section 125 deductions</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-slate-900">Instant Confirmation</p>
                    <p className="text-sm text-slate-600">Receive enrollment documents and confirmation immediately</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="text-center space-y-4"
        >
          <Link to={createPageUrl('BenefitsCatalog')}>
            <Button className="h-16 px-12 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-xl">
              Start Browsing Benefits
              <ArrowRight className="w-5 h-5 ml-3" />
            </Button>
          </Link>
          <p className="text-sm text-slate-500">
            This should take about 10-15 minutes to complete
          </p>
        </motion.div>
      </div>

      {/* AI Chat Widget */}
      <EnrollmentAIChat context={{ currentPage: 'intro', employeeData: employee }} />
    </div>
  );
}